import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ProjectApi from './projectApi';
import { Project } from './projectType';
import HorizontalMenu from '../components/common/horizontalMenu';
import { Trash } from 'lucide-react';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';

const DeleteProjectPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
    { label: 'Delete', path: '/main/project/delete' },
  ];

  const location = useLocation();
  const navigate = useNavigate();
  const selectedProject = location.state?.selectedProject as Project | undefined;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [project, setProject] = useState<Partial<Project>>({
    project_id: '',
    name: '',
    project_type: '',
    start_date: '',
    end_date: '',
  });

  // Check if selectedProject is available
  useEffect(() => {
    if (!selectedProject) {
      setErrorMessage('Project not available, please select a project by clicking Go To Project button.');
    } else {
      setProject({
        project_id: selectedProject.project_id || '',
        name: selectedProject.name || '',
        project_type: selectedProject.project_type || '',
        start_date: selectedProject.start_date || '',
        end_date: selectedProject.end_date || '',
      });
    }
  }, [selectedProject]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!selectedProject) {
      setErrorMessage('Project not available, cannot delete');
      return;
    }

    try {
      if (!project.project_id) {
        throw new Error('Project ID is missing. Unable to delete project.');
      }
      await ProjectApi.delete(String(project.project_id));
      setMessage('Project deleted successfully');
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unknown error occurred.');
      }
    }
  };

  const handleNavigateToProjectList = () => {
    navigate('/main/project');
  };

  return (
    <>
      <HorizontalMenu />
      <div className="container mx-auto max-w-7xl px-4 py-6">
        <Breadcrumbs items={breadcrumbs} />

        <h2 className="text-2xl font-semibold mb-6">Delete Project</h2>

        {errorMessage && !selectedProject && (
          <div className="space-y-4">
            <Alert variant="error">{errorMessage}</Alert>
            <Button onClick={handleNavigateToProjectList} size="medium">
              Go to Project
            </Button>
          </div>
        )}

        {message ? (
          <div className="space-y-4">
            <Alert variant="success">{message}</Alert>
            <Button onClick={handleNavigateToProjectList} size="medium">
              Return to Projects
            </Button>
          </div>
        ) : selectedProject ? (
          <div className="max-w-2xl space-y-6">
            {/* Warning Message */}
            <Alert variant="warning">
              If you delete the project, all estimates and itemized estimates, invoices and itemized
              invoices linked to the project will be deleted as well.
            </Alert>

            <form onSubmit={handleSubmit} className="space-y-6">
              {errorMessage && selectedProject && <Alert variant="error">{errorMessage}</Alert>}

              {/* Project Information */}
              <div className="p-4 border-2 border-gray-300 rounded-lg bg-white space-y-3">
                <h3 className="text-lg font-semibold mb-3">Project Information</h3>
                <div>
                  <span className="font-semibold">Name:</span>{' '}
                  <span className="text-paragraph">{project?.name}</span>
                </div>
                <div>
                  <span className="font-semibold">Project Type:</span>{' '}
                  <span className="text-paragraph">{project?.project_type}</span>
                </div>
                <div>
                  <span className="font-semibold">Start Date:</span>{' '}
                  <span className="text-paragraph">{project?.start_date}</span>
                </div>
                <div>
                  <span className="font-semibold">End Date:</span>{' '}
                  <span className="text-paragraph">{project?.end_date}</span>
                </div>
              </div>

              {/* Delete Button */}
              <Button
                type="submit"
                size="medium"
                className="bg-red-600 hover:bg-red-700 active:bg-red-800"
              >
                <Trash size={16} />
                Delete Project
              </Button>
            </form>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default DeleteProjectPage;